import type { Recording } from '@/types/documentation'

const PLACEHOLDER_NAMES = new Set(['New Transcript', 'Untitled'])
const PLACEHOLDER_PATTERNS = [
  /^watch-[0-9a-f-]+\.(wav|m4a|pcm)$/i,
  /^recording-[0-9a-f-]+\.(wav|m4a|pcm)$/i,
  // macOS capture: `mac-YYYYMMDD-HHMMSS.wav` (#1156). The stamp is an exact
  // shape, so it is matched literally rather than as loose hex — a user title
  // that happens to spell hex (`mac-face.wav`) stays the user's. Keep in sync
  // with `_PLACEHOLDER_PATTERNS` in app/schemas/recording.py.
  /^mac-\d{8}-\d{6}\.(wav|m4a|pcm)$/i,
]

export function isPlaceholderName(name: string | null | undefined): boolean {
  if (!name) return true
  const trimmed = name.trim()
  if (!trimmed) return true
  if (PLACEHOLDER_NAMES.has(trimmed)) return true
  return PLACEHOLDER_PATTERNS.some((r) => r.test(trimmed))
}

function formatRecordingDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function getRecordingDisplayName(
  recording: Pick<Recording, 'name' | 'dictation' | 'recorded_at' | 'created_at'>,
): string {
  const stored = recording.name?.trim()
  if (stored && !isPlaceholderName(stored)) return stored
  const prefix =
    recording.dictation === true
      ? 'Dictation'
      : recording.dictation === false
        ? 'Conversation'
        : 'Recording'
  const iso = recording.recorded_at ?? recording.created_at
  if (!iso) return prefix
  return `${prefix} on ${formatRecordingDateTime(iso)}`
}
